import React, { useEffect, useState, useMemo } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, TextField, Box, Link, TablePagination, TableSortLabel
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getDealersPendingList, verifyDealer } from "../services/dealerServices";

export default function DealerSummaryAdminTool() {
  const navigate = useNavigate();
  const [pendingList, setPendingList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [dealer, setDealer] = useState(null); // Assuming setDealer is part of your state
  const [searchText, setSearchText] = useState("");

  // Sorting State
  const [orderBy, setOrderBy] = useState('dealerId');
  const [order, setOrder] = useState('asc');

  useEffect(() => {
    setLoading(true);
    getDealersPendingList().then((res) => {
      if (res.data?.pendinglist) {
        setPendingList(res.data.pendinglist);
      }
      setLoading(false);
    });
  }, []);

  // --- Click Logic Integration ---
  const handleCellClick = (field, value, rowDealerId) => {
    console.log(`Selected column : ${field} and cell value ${value}`);

    // Prevent action if value is 0, null, or undefined
    if (value === 0 || !value) return;

    /**
     * Helper to handle API verification and navigation
     * @param {number} tabKey - The tab index to open in DealerDetails
     * @param {string} filter - The radio button filter to set in AgreementsGrid
     */
    const processAndNavigate = (tabKey, filter) => {
      setLoading(true);
      verifyDealer(rowDealerId)
        .then((re) => {
          const dealerData = re.data;
          if (dealerData && dealerData.length > 0) {
            // 1. Sync local state and storage
            setDealer(dealerData[0]);
            localStorage.setItem("dealer", JSON.stringify(dealerData[0]));

            setLoading(false);

            // 2. Navigate with both the tab key and the initial filter status
            navigate(`/home/dealerdetails`, {
              state: {
                key: tabKey,
                initialFilter: filter
              }
            });
          } else {
            setLoading(false);
            console.warn("No dealer data returned from verification.");
          }
        })
        .catch((err) => {
          setLoading(false);
          console.error("Dealer verification failed:", err);
        });
    };

    // Logic mapping based on the clicked column field
    switch (field) {
      case "agreementCount":
        // Opens Tab 1 with "Pending" filter
        processAndNavigate(1, "Pending");
        break;

      case "savedagreementCount":
        // Opens Tab 1 with "Saved" filter
        processAndNavigate(1, "Saved");
        break;

      case "claimCount":
        // Opens Tab 2 with "Saved" filter (or "Paid" if applicable)
        processAndNavigate(2, "Pending");
        break;

      default:
        console.log("No action defined for field:", field);
        break;
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

const filteredList = useMemo(() => {
  if (!searchText) return pendingList;

  const search = searchText.toLowerCase().trim();

  return pendingList
    .map((dealer) => {
      const tenants = JSON.parse(dealer.tenantDetails || "[]");

      const dealerMatch =
        dealer.dealerId?.toString().toLowerCase().includes(search) ||
        dealer.dealerName?.toLowerCase().includes(search);

      const matchedTenants = tenants.filter(
        (t) =>
          t.tenantId?.toString().toLowerCase().includes(search) ||
          t.tenantName?.toLowerCase().includes(search)
      );

      if (dealerMatch) {
        return dealer; // show all tenants if dealer matches
      }

      if (matchedTenants.length > 0) {
        return {
          ...dealer,
          tenantDetails: JSON.stringify(matchedTenants),
        };
      }

      return null;
    })
    .filter(Boolean);
}, [pendingList, searchText]);

  // Manual Sorting with Nested Tenant Sort
  const sortedData = useMemo(() => {
    const dealerFields = ['dealerId', 'dealerName', 'needMapping', 'pendingApproval'];
    const isDealerField = dealerFields.includes(orderBy);

    return [...filteredList].map(d => {
      const tenants = JSON.parse(d.tenantDetails || "[]");
      if (!isDealerField) {
        tenants.sort((a, b) => {
          let vA = a[orderBy], vB = b[orderBy];
          if (typeof vA === 'string') { vA = vA.toLowerCase(); vB = vB.toLowerCase(); }
          return order === 'asc' ? (vA < vB ? -1 : 1) : (vB < vA ? -1 : 1);
        });
      }
      return { ...d, parsedTenants: tenants };
    }).sort((a, b) => {
      let aVal, bVal;
      if (isDealerField) {
        aVal = a[orderBy]; bVal = b[orderBy];
      } else {
        aVal = a.parsedTenants.length > 0 ? a.parsedTenants[0][orderBy] : "";
        bVal = b.parsedTenants.length > 0 ? b.parsedTenants[0][orderBy] : "";
      }
      if (typeof aVal === 'string') { aVal = aVal.toLowerCase(); bVal = bVal.toLowerCase(); }
      return order === 'asc' ? (aVal < bVal ? -1 : 1) : (bVal < aVal ? -1 : 1);
    });
  }, [filteredList, order, orderBy]);

  const currentPageData = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const emptyRows = Math.max(0, rowsPerPage - currentPageData.length);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden', p: 1 }}>
      <Box sx={{ mb: 2, display: "flex" }}>
        <TextField
          label="Search DealerId / TenantId"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(0);
          }}
          sx={{ width: 300 }}
        />
      </Box>
      <TableContainer component={Paper} sx={{ height: 600, maxWidth: '100%', overflowX: 'auto', border: '1px solid #e0e0e0' }}>
        <Table stickyHeader size="small" sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              {[
                { id: 'dealerId', label: 'DealerId' },
                { id: 'dealerName', label: 'DealerName' },
                { id: 'needMapping', label: 'Agreements (Sync)' },
                { id: 'pendingApproval', label: 'Agreements (Approve)' },
                { id: 'tenantId', label: 'Tenant Id' },
                { id: 'tenantName', label: 'Tenant Name' },
                { id: 'agreementCount', label: 'Pending Agreements' },
                { id: 'savedagreementCount', label: 'Saved Agreements' },
                { id: 'claimCount', label: 'Potential Claims' }
              ].map((col) => (
                <TableCell key={col.id} sx={{ fontWeight: 'bold', backgroundColor: '#fff' }}>
                  <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} onClick={() => handleRequestSort(col.id)}>
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPageData.map((dealerItem) => {
              const tenants = dealerItem.parsedTenants;
              const firstT = tenants[0];
              const others = tenants.slice(1);

              return (
                <React.Fragment key={dealerItem.dealerId}>
                  {/* Dealer Header Row */}
                  <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>{dealerItem.dealerId}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{dealerItem.dealerName}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{dealerItem.needMapping}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{dealerItem.pendingApproval}</TableCell>
                    <TableCell>{firstT?.tenantId || ""}</TableCell>
                    <TableCell>{firstT?.tenantName || ""}</TableCell>
                    <TableCell>
                      {firstT && (
                        <Link onClick={() => handleCellClick("agreementCount", firstT.agreementCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer', fontWeight: 'bold', textDecoration: 'underline' }}>
                          {firstT.agreementCount}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell>
                      <Link onClick={() => handleCellClick("savedagreementCount", firstT?.savedagreementCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer' }}>
                        {firstT?.savedagreementCount || ""}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Link onClick={() => handleCellClick("claimCount", firstT?.claimCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer' }}>
                        {firstT?.claimCount || ""}
                      </Link>
                    </TableCell>
                  </TableRow>

                  {/* Nested Tenant Rows */}
                  {others.map((t) => (
                    <TableRow key={t.tenantId} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
                      <TableCell colSpan={4} />
                      <TableCell>{t.tenantId}</TableCell>
                      <TableCell>{t.tenantName}</TableCell>
                      <TableCell>
                        <Link onClick={() => handleCellClick("agreementCount", t.agreementCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer', textDecoration: 'underline' }}>
                          {t.agreementCount}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link onClick={() => handleCellClick("savedagreementCount", t.savedagreementCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer' }}>
                          {t.savedagreementCount}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Link onClick={() => handleCellClick("claimCount", t.claimCount, dealerItem.dealerId)} sx={{ color: '#673ab7', cursor: 'pointer' }}>
                          {t.claimCount}
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              );
            })}
            {emptyRows > 0 && Array.from(new Array(emptyRows)).map((_, i) => (
              <TableRow key={`empty-${i}`} style={{ height: 43 }}><TableCell colSpan={9} /></TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={filteredList.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </Box>
  );
}